import { Alert, Button, Collapse, Form, Input, Modal, Select, Space, Table, Tag, Typography, App as AntApp } from "antd";
import { EditOutlined, LockOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { attendanceKeys, attendanceStatusColors, markStatuses, type AttendanceStatus, type LessonAttendance, type StudentAttendance } from "../app/attendance.ts";
import { useAuth } from "../app/auth.tsx";
import { formatDate } from "../app/classes.ts";
import type { LessonDetails } from "../app/lessons.ts";
import { formatSchoolDateTime } from "../app/time.ts";
import { AuditHistory } from "./AuditHistory.tsx";

interface Props {
  classId: string;
  lesson: LessonDetails | undefined;
  onClose(): void;
}

/** Field names match the API (status, reason) so server errors land on the inputs. */
interface EditForm {
  status?: AttendanceStatus;
  reason?: string;
}

const REASON_MAX_LENGTH = 500;

const studentName = (s: StudentAttendance) => [s.lastName, s.firstName, s.middleName].filter(Boolean).join(" ");

/**
 * A lesson's attendance (GET /attendance/lessons/{id}): the roster on the lesson date with each status, who marked it and when.
 * Staff holding attendance.manage may change a status (after the deadline with a reason); those also holding attendance.excuse
 * may set or withdraw an excused absence with a reason. Users holding audit.read also see the change history of the lesson's
 * attendance. The roster arrives in Uzbek alphabetical order and is shown as is. The server decides; the dialog only hides
 * what the user cannot do.
 */
export function LessonAttendanceModal({ classId, lesson, onClose }: Props) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("attendance.manage");
  const canExcuse = hasPermission("attendance.excuse");
  const canReadAudit = hasPermission("audit.read");
  const [editing, setEditing] = useState<StudentAttendance>();

  const attendance = useQuery({
    queryKey: attendanceKeys.lesson(classId, lesson?.id ?? ""),
    queryFn: async () => {
      const { data, error } = await api.GET("/attendance/lessons/{lessonId}", { params: { path: { lessonId: lesson?.id ?? "" } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    enabled: lesson !== undefined,
  });

  const data = attendance.data;
  // Audit entries of the lesson's attendance name the record; show whose record it is.
  const describeRecord = (recordId: string) => {
    const student = data?.students.find((s) => s.recordId === recordId);
    return student ? studentName(student) : undefined;
  };
  const editable = (s: StudentAttendance) => canManage && !s.isLocked && data?.lessonStatus !== "Cancelled";

  const columns = [
    { title: t("attendance.student"), key: "student", render: (_: unknown, s: StudentAttendance) => (
      <Space orientation="vertical" size={0}>
        <span>{studentName(s)}</span>
        {!s.onRoster && <Typography.Text type="secondary">{t("attendance.offRoster")}</Typography.Text>}
      </Space>
    ) },
    { title: t("attendance.status"), key: "status", render: (_: unknown, s: StudentAttendance) => (
      <Space orientation="vertical" size={0}>
        <Space size={4}>
          {s.status ? <Tag color={attendanceStatusColors[s.status]}>{t(`attendance.statuses.${s.status}`)}</Tag> : <Tag>{t("attendance.unmarked")}</Tag>}
          {s.isLocked && <LockOutlined aria-label={t("attendance.locked")} title={t("attendance.locked")} />}
        </Space>
        {s.adjustedStatus && <Typography.Text type="secondary">{t("attendance.adjustedTo", { status: t(`attendance.statuses.${s.adjustedStatus}`) })}</Typography.Text>}
        {s.reason && <Typography.Text type="secondary">{s.reason}</Typography.Text>}
      </Space>
    ) },
    { title: t("attendance.markedBy"), key: "markedBy", render: (_: unknown, s: StudentAttendance) =>
      s.markedAt ? (
        <Space orientation="vertical" size={0}>
          <span>{s.markedByName}</span>
          <Typography.Text type="secondary">{formatSchoolDateTime(s.markedAt)}</Typography.Text>
        </Space>
      ) : null },
    ...(canManage
      ? [{ title: t("common.actions"), key: "actions", render: (_: unknown, s: StudentAttendance) =>
          editable(s) ? <Button icon={<EditOutlined />} aria-label={t("attendance.editFor", { name: studentName(s) })} onClick={() => setEditing(s)} /> : null }]
      : []),
  ];

  return (
    <Modal open={lesson !== undefined} title={t("attendance.title")} onCancel={onClose} footer={<Button onClick={onClose}>{t("common.close")}</Button>} width={760} destroyOnHidden>
      {lesson && (
        <Typography.Paragraph type="secondary">
          {t("lessons.lessonLabel", { subject: lesson.subjectName, date: formatDate(lesson.date), period: Number(lesson.period) })}
        </Typography.Paragraph>
      )}
      {attendance.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void attendance.refetch()}>{t("app.retry")}</Button>} />}
      {data && (
        <Space orientation="vertical" size="small" style={{ width: "100%" }}>
          <Space wrap size={8}>
            <Typography.Text>{t("attendance.deadline", { time: formatSchoolDateTime(data.deadline) })}</Typography.Text>
            {data.isPastDeadline && <Tag color="orange">{t("attendance.pastDeadline")}</Tag>}
            {!data.isMarked && data.lessonStatus !== "Cancelled" && <Tag>{t("attendance.notMarked")}</Tag>}
            {data.lessonStatus === "Cancelled" && <Tag color="red">{t("lessons.statuses.Cancelled")}</Tag>}
          </Space>
          <Table<StudentAttendance>
            rowKey="studentId"
            size="small"
            columns={columns}
            dataSource={data.students}
            pagination={false}
            locale={{ emptyText: t("attendance.emptyRoster") }}
            scroll={{ x: "max-content" }}
          />
          {canReadAudit && lesson && (
            <Collapse
              size="small"
              items={[{ key: "history", label: t("attendance.history"), children: <AuditHistory entityType="Lesson" entityId={lesson.id} includeChildren describe={describeRecord} /> }]}
            />
          )}
        </Space>
      )}
      {attendance.isPending && lesson !== undefined && <Table<StudentAttendance> rowKey="studentId" size="small" columns={columns} dataSource={[]} loading pagination={false} />}
      {data && (
        <EditAttendanceModal classId={classId} attendance={data} student={editing} canExcuse={canExcuse} onClose={() => setEditing(undefined)} />
      )}
    </Modal>
  );
}

interface EditProps {
  classId: string;
  attendance: LessonAttendance;
  student: StudentAttendance | undefined;
  canExcuse: boolean;
  onClose(): void;
}

/** One student's status (PUT /attendance/lessons/{lessonId}/students/{studentId}); a reason is asked for whenever the server needs one. */
function EditAttendanceModal({ classId, attendance, student, canExcuse, onClose }: EditProps) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<EditForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const status = Form.useWatch("status", form);

  useEffect(() => {
    if (!student) return;
    form.resetFields();
    form.setFieldsValue({ status: student.status ?? "Present" });
    setSummary([]);
  }, [student, form]);

  const statuses: AttendanceStatus[] = [...markStatuses, ...(canExcuse ? (["Excused"] as const) : [])];
  const touchesExcuse = (status === "Excused") !== (student?.status === "Excused");
  const reasonRequired = attendance.isPastDeadline || touchesExcuse;

  const mutation = useMutation({
    mutationFn: async (values: EditForm) => {
      const { data, error, response } = await api.PUT("/attendance/lessons/{lessonId}/students/{studentId}", {
        params: { path: { lessonId: attendance.lessonId, studentId: student?.studentId ?? "" } },
        body: { status: values.status ?? null, reason: values.reason?.trim() || null },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (saved) => {
      // The server keeps a record whose status is already the chosen one untouched (not even the reason), so say so.
      const after = saved.students.find((s) => s.studentId === student?.studentId);
      if (student?.status && after?.status === student.status && after.markedAt === student.markedAt) void message.info(t("attendance.noChange"));
      queryClient.setQueryData(attendanceKeys.lesson(classId, attendance.lessonId), saved);
      await Promise.all([queryClient.invalidateQueries({ queryKey: attendanceKeys.all(classId) }), queryClient.invalidateQueries({ queryKey: ["audit"] })]);
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  return (
    <Modal
      open={student !== undefined}
      title={student ? t("attendance.editTitle", { name: studentName(student) }) : ""}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={mutation.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {attendance.isPastDeadline && <Alert type="warning" showIcon title={t("attendance.afterDeadlineHint")} style={{ marginBottom: 16 }} />}
      {summary.length > 0 && <Alert type="error" showIcon title={summary.join(" ")} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate(values)}>
        <Form.Item name="status" label={t("attendance.status")} rules={[{ required: true, message: t("attendance.statusRequired") }]}>
          <Select options={statuses.map((s) => ({ value: s, label: t(`attendance.statuses.${s}`) }))} />
        </Form.Item>
        <Form.Item
          name="reason"
          label={t("attendance.reason")}
          rules={[{ required: reasonRequired, whitespace: true, message: t("attendance.reasonRequired") }, { max: REASON_MAX_LENGTH }]}
          extra={reasonRequired ? t("attendance.reasonHint") : undefined}
        >
          <Input.TextArea rows={2} maxLength={REASON_MAX_LENGTH} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
